import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import axios from "axios";
import { Clock, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isPremium } = useAuth();
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${minutes}min`;
  };

  const getDiscountedPrice = (price) => {
    return isPremium ? (price * 0.85).toFixed(2) : price.toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="services-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-4 font-['Chivo']">
            Unsere <span className="text-cyan-400">Services</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            Professionelle Fahrzeugaufbereitung für jeden Anspruch. 
            Wählen Sie aus unserem umfangreichen Angebot.
          </p>
          {isPremium && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-sm">
              <span className="text-cyan-400 text-sm font-medium">
                Premium-Rabatt: 15% auf alle Services
              </span>
            </div>
          )}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {services.map((service) => (
            <div
              key={service.id}
              className="service-card bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden hover:border-cyan-500/50 flex flex-col md:flex-row"
              data-testid={`service-${service.id}`}
            >
              <div className="img-zoom w-full md:w-2/5 h-48 md:h-auto">
                <img
                  src={service.image_url}
                  alt={service.name_de}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-white">
                    {service.name_de}
                  </h3>
                  <span className="px-2 py-1 text-xs bg-zinc-800 text-zinc-400 rounded-sm capitalize">
                    {service.category === "interior" ? "Innen" : 
                     service.category === "exterior" ? "Außen" : "Komplett"}
                  </span>
                </div>
                <p className="text-sm text-zinc-500 mb-4 flex-grow">
                  {service.description_de}
                </p>
                <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatDuration(service.duration_minutes)}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-zinc-500">{t("services_from")}</span>
                    <span className="text-2xl font-bold text-white price-tag">
                      {getDiscountedPrice(service.price)}€
                    </span>
                    {isPremium && (
                      <span className="text-sm text-zinc-500 line-through">
                        {service.price.toFixed(2)}€
                      </span>
                    )}
                  </div>
                  <Link to={`/booking?service=${service.id}`}>
                    <Button
                      className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium btn-glow"
                      data-testid={`book-service-${service.id}`}
                    >
                      Buchen
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-zinc-500 mb-4">
            Fragen zu unseren Services? Kontaktieren Sie uns gerne.
          </p>
          <Link to="/booking">
            <Button
              size="lg"
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold btn-glow"
              data-testid="services-booking-cta"
            >
              Jetzt Termin buchen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
